import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowDown } from "lucide-react";
import { IMAGES } from "../data/content";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.1, delay: 0.5 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden"
    >
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMAGES.hero}
          alt={t.hero.imageAlt}
          className="h-full w-full object-cover object-[center_38%]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E1420]/85 via-[#0E1420]/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E1420]/78 via-[#0E1420]/10 to-[#0E1420]/30" />
      </motion.div>

      <motion.div
        style={{ y: contentY, opacity: fade }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-[1400px] flex-col justify-end px-6 md:px-10 pb-16 pt-32"
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 0.4 }}
          className="overline text-[#D9B87A] mb-6"
        >
          {t.hero.overline}
        </motion.div>

        <h1 className="font-serif-display font-light text-[#F6F5F1] leading-[0.92] text-[clamp(3rem,11vw,10rem)] tracking-tight">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span
                custom={i}
                variants={lineVariants}
                initial="hidden"
                animate="visible"
                className="block"
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-end">
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 text-[#F6F5F1]/90 text-base md:text-lg font-light leading-relaxed max-w-xl"
          >
            {t.hero.intro}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-6 lg:col-start-7 grid grid-cols-3 gap-4 border-t border-white/25 pt-6"
            data-testid="hero-stats"
          >
            {t.hero.stats.map((s, i) => (
              <div key={i}>
                <div className="font-serif-display text-[#F6F5F1] text-3xl md:text-5xl leading-none">
                  {s.value}
                </div>
                <div className="mt-2 text-[#D9B87A] text-[0.68rem] md:text-xs uppercase tracking-[0.14em] leading-snug">
                  {s.label}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.6 }}
        className="absolute bottom-8 right-6 md:right-10 z-10 flex items-center gap-2 text-[#F6F5F1]/80"
      >
        <span className="overline text-[0.6rem]">{t.hero.scroll}</span>
        <motion.span
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown size={16} strokeWidth={1.4} />
        </motion.span>
      </motion.div>
    </section>
  );
};
